/**
 * NotificationExclusiveSubscriptionItem
 *
 * Covers the 8 exclusive-subscription notification types in one component,
 * porting `notification_exclusive_subscription_item.dart:28-220` (the single
 * widget the Flutter router maps all 8 cases to,
 * `notification_item.dart:119-136`).
 *
 * Title varies by type (and, for renewed / expired / request-accepted /
 * request-declined / failed-insufficient, by whether `actionMaker` is the
 * current user). A coins+date subtitle line is shown only for the "subscribed"
 * and "renewed" cases (`showPaidCoins`); otherwise the subtitle is just the
 * date. Avatar from `usersInfo.userId`; no trailing thumbnail.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationAvatar } from './shared/NotificationAvatar';
import { NotificationCoinsDateLine } from './shared/NotificationCoinsDateLine';
import type { NotificationItemProps } from './notificationItemProps';

interface NotificationExclusiveSubscriptionItemProps extends NotificationItemProps {
  /** Current user's firebase id, for the `actionMaker == currentUser` branches. */
  currentUserId?: string | null;
}

export const NotificationExclusiveSubscriptionItem: React.FC<
  NotificationExclusiveSubscriptionItemProps
> = ({ notification, currentUserId, onUserTap, onItemTap }) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const n = l10n.inbox.notifications;

  const { info } = notification;
  const name = info.usersInfo.name;
  const isUndo = info.isUndo === true;
  const isActionMakerSelf = !!currentUserId && info.actionMaker === currentUserId;
  const type = info.notificationType as AppNotificationType;

  let title: string;
  let showPaidCoins = false;

  switch (type) {
    case AppNotificationType.exclusiveSubscription:
      if (isUndo) {
        title = n.exclusiveSubscriptionCancelled;
      } else {
        showPaidCoins = true;
        title = n.exclusiveSubscribed;
      }
      break;
    case AppNotificationType.exclusiveSubscriptionRenewd:
      title = isActionMakerSelf ? n.exclusiveRenewedSelf : n.exclusiveRenewedOther;
      showPaidCoins = true;
      break;
    case AppNotificationType.exclusiveSubscriptionExpired:
      title = isActionMakerSelf ? n.exclusiveExpiredSelf : n.exclusiveExpiredOther;
      break;
    case AppNotificationType.exclusiveSubscriptionRequested:
      title = n.exclusiveRequested;
      break;
    case AppNotificationType.exclusiveSubscriptionRequestCancelled:
      title = n.exclusiveRequestCancelled;
      break;
    case AppNotificationType.exclusiveSubscriptionRequestAccepted:
      title = isActionMakerSelf
        ? n.exclusiveRequestAcceptedSelf
        : n.exclusiveRequestAcceptedOther;
      break;
    case AppNotificationType.exclusiveSubscriptionRequestDeclined:
      title = isActionMakerSelf
        ? n.exclusiveRequestDeclinedSelf
        : n.exclusiveRequestDeclinedOther;
      break;
    case AppNotificationType.exclusiveSubscriptionFailedInsufficientCoins:
      title = isActionMakerSelf
        ? n.exclusiveFailedInsufficientSelf
        : n.exclusiveFailedInsufficientOther;
      break;
    default:
      // Mirrors Flutter's `default` branch (line 119-128).
      if (isUndo) {
        title = n.exclusiveUnsubscribed;
      } else {
        showPaidCoins = true;
        title = n.exclusiveSubscribed;
      }
      break;
  }

  title = title.replace('{name}', name);

  const date = formatDate(info.timestamp);
  const subtitle = showPaidCoins ? (
    <NotificationCoinsDateLine
      amount={MathUtils.removeTrailingZero(info.subscriptionInfo?.coins ?? 0)}
      date={date}
    />
  ) : (
    date
  );

  return (
    <NotificationRowLayout
      leading={<NotificationAvatar userId={info.usersInfo.userId} alt={name} />}
      title={title}
      subtitle={subtitle}
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
      onLeadingTap={onUserTap ? () => onUserTap(info.usersInfo.userId) : undefined}
    />
  );
};
